import dayjs from 'dayjs'
import { b64toBlob, getUsersBrowserData, parseDate } from '~/utils/helpers'

interface AnimalGroupEntry {
  group: number
  avgCowsNumberFedWithAgoline?: number
  dailyMineralIntakePerCow?: number
}

export interface FeedPurchaseData {
  animalGroup?: string
  purchaseDate?: string
  farmerId?: string
  feedSupplierId?: number
  customerMix?: number
  feedProductId?: number
  agolineContent?: number
  purchasedFeedAmount?: number
  purchaseReceiptFile?: {
    data: string
    name: string
  }
  feedingFrom?: string
  feedingTo?: string
  animalGroups?: AnimalGroupEntry[]
}

export function createPurchaseFormData(data: FeedPurchaseData): FormData {
  data.feedingFrom = parseDate(data.feedingFrom)
  data.feedingTo = parseDate(data.feedingTo)
  data.purchaseDate = parseDate(data.purchaseDate)
  const formData = new FormData()
  const browserData = getUsersBrowserData()

  Object.keys(browserData).forEach((key) => {
    formData.append(key, browserData[key as keyof typeof browserData])
  })
  formData.append('animalGroup', data.animalGroup?.toString() || '')
  formData.append('purchaseDate', data.purchaseDate || '')
  formData.append('feedSupplierId', data.feedSupplierId?.toString() || '')
  formData.append('customerMix', data.customerMix?.toString() || '')
  formData.append('purchasedFeedAmount', data.purchasedFeedAmount?.toString() || '')
  formData.append('feedingFrom', data.feedingFrom || '')
  formData.append('feedingTo', data.feedingTo || '')
  if (data?.farmerId) {
    formData.append('farmerId', data.farmerId || '')
  }

  if (data.customerMix === 0 && data.feedProductId) {
    formData.append('feedProductId', data.feedProductId.toString())
  }

  if (data.customerMix === 1 && data.agolineContent !== undefined) {
    formData.append('agolineContent', data.agolineContent.toString())
  }

  if (data.animalGroups) {
    data.animalGroups.forEach((item, index) => {
      Object.entries(item).forEach(([key, value]) => {
        if (value) {
          formData.append(`animalGroups[${index}][${key}]`, value);
        }
      });
    });
  }
  // if it starts with data: it means new file was uploaded
  if (data.purchaseReceiptFile?.data && data.purchaseReceiptFile.data.startsWith('data:')) {
    const mimeType = data.purchaseReceiptFile.data.split(';')[0].split('data:')[1]
    const blob = b64toBlob(data.purchaseReceiptFile.data, mimeType)
    formData.append('purchaseReceiptFile', blob, data.purchaseReceiptFile.name)
  }

  return formData
}

export const getFileIcon = (fileName?: string): string => {
  if (!fileName) return 'file alternate outline'
  const ext = fileName.split('.').pop()?.toLowerCase()
  switch (ext) {
    case 'pdf':
      return 'file pdf outline'
    case 'xlsx':
    case 'xls':
      return 'file excel outline'
    case 'jpg':
    case 'gif':
    case 'jpeg':
    case 'png':
    case 'bmp':
      return 'file image outline'
    default:
      return 'file alternate outline'
  }
}

export const getFileTypeLabel = (fileName?: string): string => {
  if (!fileName) return 'Document'
  const ext = fileName.split('.').pop()?.toLowerCase()
  switch (ext) {
    case 'pdf':
      return 'PDF'
    case 'xlsx':
    case 'xls':
      return 'Excel'
    case 'jpg':
    case 'jpeg':
      return 'JPEG'
    case 'gif':
      return 'GIF'
    case 'png':
      return 'PNG'
    case 'bmp':
      return 'BMP'
    default:
      return 'Document'
  }
}

export const getDateFormatted = (date: string | undefined): string => {
  if (!date) return ''
  return dayjs(date, 'DD.MM.YYYY').format('YYYY-MM-DD')
}

export const getMinDate = (startDate: string | undefined): string => {
  if (!startDate) return dayjs().format('DD.MM.YYYY')
  return dayjs(startDate, 'DD.MM.YYYY').add(1, 'day').format('DD.MM.YYYY')
}
